import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchDemoDrLocus, fetchDemoHq, type DemoV3DrLocus, type DemoV3Hq } from "../../../api/demoV3";

/**
 * Handling-qualities margins (VERTICAL_DEMOS_V2 D4): the aircraft's dynamic
 * modes against the MIL-STD-1797A Cat B Level boundaries.
 *
 *  (a) Dutch roll — ζ on x, ωn on y, with the L1/L2 boundary curves
 *      (ζ_min verticals + ζ·ωn hyperbolae + ωn_min floor), the aircraft point
 *      and the battery-station degradation locus with its Level-1 crossing.
 *  (b) Short period — ζ vs CAP (log y) with the L1/L2 boxes and the point.
 *
 * Hand-rolled SVG; every color resolves through the --v5-* tokens.
 */

const W = 250;
const H = 210;
const PAD = { left: 34, right: 8, top: 10, bottom: 26 };

function levelClass(level: number): string {
  return level <= 1 ? "is-l1" : level === 2 ? "is-l2" : "is-l3";
}

function fmt(value: number, digits = 2): string {
  return Number.isFinite(value) ? value.toFixed(digits) : "—";
}

function DutchRollChart({ hq, locus }: { hq: DemoV3Hq; locus: DemoV3DrLocus | null }) {
  const dr = hq.hq.dutch_roll;
  const bounds = dr.boundaries;

  const geometry = useMemo(() => {
    const zetas = [dr.zeta, ...(locus?.points ?? []).map((p) => p.dutch_roll.zeta)];
    const wns = [dr.wn_rad_s, ...(locus?.points ?? []).map((p) => p.dutch_roll.wn_rad_s)];
    const xMax = Math.max(0.12, ...zetas) * 1.35;
    const yMax = Math.max(1.0, ...wns) * 1.25;
    const px = (zeta: number) => PAD.left + (zeta / xMax) * (W - PAD.left - PAD.right);
    const py = (wn: number) => H - PAD.bottom - (wn / yMax) * (H - PAD.top - PAD.bottom);
    return { xMax, yMax, px, py };
  }, [dr, locus]);

  const { xMax, yMax, px, py } = geometry;

  // ζ·ωn = const hyperbola sampled as a polyline within the viewport.
  const hyperbola = (zetaWnMin: number): string => {
    const pts: string[] = [];
    for (let i = 0; i <= 60; i += 1) {
      const zeta = 0.004 + (i / 60) * (xMax - 0.004);
      const wn = zetaWnMin / zeta;
      if (wn > yMax || wn <= 0) continue;
      pts.push(`${px(zeta).toFixed(1)},${py(Math.min(wn, yMax)).toFixed(1)}`);
    }
    return pts.join(" ");
  };

  const crossing = locus?.points.find((p) => p.fraction === locus.crossing_fraction) ?? null;

  return (
    <figure className="v5-hq-chart" data-testid="v5-hq-dutch-roll">
      <figcaption>Dutch roll — ζ vs ω<sub>n</sub></figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="v5-demo-chart" role="img" aria-label="Dutch roll damping vs frequency against Level 1 and Level 2 boundaries">
        <line className="v5-demo-axis" x1={PAD.left} y1={H - PAD.bottom} x2={W - PAD.right} y2={H - PAD.bottom} />
        <line className="v5-demo-axis" x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={H - PAD.bottom} />
        <text className="v5-demo-axis-label" x={(PAD.left + W - PAD.right) / 2} y={H - 6}>ζ</text>
        <text className="v5-demo-axis-label" x={10} y={(PAD.top + H - PAD.bottom) / 2} transform={`rotate(-90 10 ${(PAD.top + H - PAD.bottom) / 2})`}>ωn (rad/s)</text>
        <text className="v5-demo-tick" x={px(bounds.L1.zeta_min)} y={H - PAD.bottom + 12}>{bounds.L1.zeta_min}</text>
        <text className="v5-demo-tick is-y" x={PAD.left - 4} y={py(1) + 3}>1</text>
        <text className="v5-demo-tick is-y" x={PAD.left - 4} y={py(2) + 3}>2</text>

        {/* L2 then L1 boundaries (verticals ζ_min, hyperbolae ζωn_min, floor ωn_min) */}
        <g className="v5-hq-boundary is-l2-line" data-testid="v5-hq-dr-l2">
          <line x1={px(bounds.L2.zeta_min)} y1={PAD.top} x2={px(bounds.L2.zeta_min)} y2={H - PAD.bottom} />
          <polyline points={hyperbola(bounds.L2.zeta_wn_min)} />
          <line x1={PAD.left} y1={py(bounds.L2.wn_min)} x2={W - PAD.right} y2={py(bounds.L2.wn_min)} />
        </g>
        <g className="v5-hq-boundary is-l1-line" data-testid="v5-hq-dr-l1">
          <line x1={px(bounds.L1.zeta_min)} y1={PAD.top} x2={px(bounds.L1.zeta_min)} y2={H - PAD.bottom} />
          <polyline points={hyperbola(bounds.L1.zeta_wn_min)} />
          <line x1={PAD.left} y1={py(bounds.L1.wn_min)} x2={W - PAD.right} y2={py(bounds.L1.wn_min)} />
        </g>

        {/* degradation locus */}
        {locus ? (
          <g data-testid="v5-hq-dr-locus">
            <polyline
              className="v5-hq-locus"
              points={locus.points.map((p) => `${px(p.dutch_roll.zeta).toFixed(1)},${py(p.dutch_roll.wn_rad_s).toFixed(1)}`).join(" ")}
            />
            {crossing ? (
              <g>
                <circle className="v5-hq-locus-crossing" cx={px(crossing.dutch_roll.zeta)} cy={py(crossing.dutch_roll.wn_rad_s)} r={3.4} />
                <text className="v5-hq-annotation" x={px(crossing.dutch_roll.zeta) - 6} y={py(crossing.dutch_roll.wn_rad_s) - 7}>
                  L1→L2 @ {Math.round(locus.crossing_fraction * 100)}%
                </text>
              </g>
            ) : null}
          </g>
        ) : null}

        {/* the aircraft */}
        <circle className={`v5-hq-point ${levelClass(dr.level)}`} cx={px(dr.zeta)} cy={py(dr.wn_rad_s)} r={4.4} data-testid="v5-hq-dr-point" />
      </svg>
    </figure>
  );
}

function ShortPeriodChart({ hq }: { hq: DemoV3Hq }) {
  const sp = hq.hq.short_period;
  const bounds = sp.boundaries;

  const capLo = 0.02;
  const capHi = 14;
  const zetaMax = 2.3;
  const px = (zeta: number) => PAD.left + (zeta / zetaMax) * (W - PAD.left - PAD.right);
  const py = (cap: number) => {
    const t = (Math.log10(cap) - Math.log10(capLo)) / (Math.log10(capHi) - Math.log10(capLo));
    return H - PAD.bottom - t * (H - PAD.top - PAD.bottom);
  };
  const box = (level: "L1" | "L2") => {
    const [z0, z1] = bounds[level].zeta;
    const [c0, c1] = bounds[level].CAP;
    return { x: px(z0), y: py(c1), width: px(z1) - px(z0), height: py(c0) - py(c1) };
  };

  return (
    <figure className="v5-hq-chart" data-testid="v5-hq-short-period">
      <figcaption>Short period — CAP vs ζ</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="v5-demo-chart" role="img" aria-label="Short period CAP vs damping against Level 1 and Level 2 boxes">
        <line className="v5-demo-axis" x1={PAD.left} y1={H - PAD.bottom} x2={W - PAD.right} y2={H - PAD.bottom} />
        <line className="v5-demo-axis" x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={H - PAD.bottom} />
        <text className="v5-demo-axis-label" x={(PAD.left + W - PAD.right) / 2} y={H - 6}>ζ</text>
        <text className="v5-demo-axis-label" x={10} y={(PAD.top + H - PAD.bottom) / 2} transform={`rotate(-90 10 ${(PAD.top + H - PAD.bottom) / 2})`}>CAP (log)</text>
        {[0.1, 1, 10].map((c) => (
          <text key={c} className="v5-demo-tick is-y" x={PAD.left - 4} y={py(c) + 3}>{c}</text>
        ))}
        {[0.5, 1, 2].map((z) => (
          <text key={z} className="v5-demo-tick" x={px(z)} y={H - PAD.bottom + 12}>{z}</text>
        ))}

        <rect className="v5-hq-boundary-box is-l2-box" {...box("L2")} data-testid="v5-hq-sp-l2" />
        <rect className="v5-hq-boundary-box is-l1-box" {...box("L1")} data-testid="v5-hq-sp-l1" />

        <circle className={`v5-hq-point ${levelClass(sp.level)}`} cx={px(sp.zeta)} cy={py(sp.CAP)} r={4.4} data-testid="v5-hq-sp-point" />
      </svg>
    </figure>
  );
}

export function HqMarginsCard({ projectId }: { projectId: string; branch?: string; state?: Record<string, unknown> }) {
  const hqQuery = useQuery({
    queryKey: ["demo-v3", projectId, "hq"],
    queryFn: () => fetchDemoHq(projectId),
    staleTime: Infinity,
    retry: false,
  });
  // The DR-degradation locus is optional (404 for airframes without one).
  const locusQuery = useQuery({
    queryKey: ["demo-v3", projectId, "dr-locus"],
    queryFn: () => fetchDemoDrLocus(projectId).catch(() => null),
    staleTime: Infinity,
    retry: false,
    enabled: hqQuery.isSuccess,
  });

  if (hqQuery.isLoading) {
    return <div className="v5-demo-card is-loading" data-tour="v5-hq-margins">Loading handling qualities…</div>;
  }
  if (hqQuery.isError || !hqQuery.data) {
    return (
      <div className="v5-demo-card is-empty" data-tour="v5-hq-margins">
        <strong>HQ margins</strong>
        <span>No handling-qualities assets for this project</span>
      </div>
    );
  }

  const hq = hqQuery.data;
  const assessment = hq.hq;
  const chips: Array<{ label: string; level: number }> = [
    { label: "Short period", level: assessment.short_period.level },
    { label: "Dutch roll", level: assessment.dutch_roll.level },
    { label: "Roll", level: assessment.roll.level },
    { label: "Spiral", level: assessment.spiral.level },
    { label: "Overall", level: assessment.overall_level },
  ];

  return (
    <div className="v5-demo-card v5-hq-margins" data-tour="v5-hq-margins" data-testid="v5-hq-margins">
      <div className="v5-demo-toolbar">
        {chips.map((chip) => (
          <span key={chip.label} className={`v5-chip v5-hq-level-chip ${levelClass(chip.level)}`} data-testid={`v5-hq-chip-${chip.label.toLowerCase().replace(/ /g, "-")}`}>
            {chip.label} · L{chip.level}
          </span>
        ))}
      </div>
      <div className="v5-demo-card-body is-charts">
        <DutchRollChart hq={hq} locus={locusQuery.data ?? null} />
        <ShortPeriodChart hq={hq} />
      </div>
      <footer className="v5-demo-footnote">
        {assessment.spec} · DR ζ {fmt(assessment.dutch_roll.zeta, 3)}, ζωn {fmt(assessment.dutch_roll.zeta_wn, 3)} · SP CAP {fmt(assessment.short_period.CAP, 2)}, ζ {fmt(assessment.short_period.zeta, 2)}
      </footer>
    </div>
  );
}
